import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EVENTS, onEvent } from "./events";

/**
 * Global "חדש" actions (top bar, Alt+1..): each one belongs to the screen that already owns
 * the battle-tested create dialog. A click while that screen is open opens its dialog in place;
 * from anywhere else it navigates there with `state.quickCreate`, and the screen opens it on arrival.
 */
export type QuickCreateType =
  | "customer"
  | "task"
  | "income"
  | "expense"
  | "form"
  | "meeting"
  | "user"
  | "automation"
  | "mailing"
  | "data_audit";

export const QUICK_CREATE_HOME: Record<QuickCreateType, string> = {
  customer: "/customers",
  task: "/tasks",
  income: "/income-expenses",
  expense: "/income-expenses",
  form: "/forms",
  meeting: "/meetings",
  user: "/users",
  automation: "/automations",
  mailing: "/mailing",
  // «בדיקת נתונים (AI)» — the route itself opens the agent's card
  data_audit: "/ai/data-audit",
};

type Handler = () => void;
const handlers = new Map<QuickCreateType, Handler>();

/** Called by the shell: run the open screen's handler, or go to the screen that owns the dialog. */
export function runQuickCreate(type: QuickCreateType, navigate: (to: string, opts?: { state?: unknown }) => void) {
  const handler = handlers.get(type);
  if (handler) {
    handler();
    return;
  }
  navigate(QUICK_CREATE_HOME[type], { state: { quickCreate: type } });
}

/** A screen registers the dialog it opens for a quick-create type (and handles arrival with state). */
export function useQuickCreate(type: QuickCreateType, handler: Handler | null | undefined) {
  const ref = useRef(handler);
  ref.current = handler;
  const location = useLocation();
  const navigate = useNavigate();
  const enabled = Boolean(handler);

  useEffect(() => {
    if (!enabled) return undefined;
    const fn = () => ref.current?.();
    handlers.set(type, fn);
    const off = onEvent<{ type?: QuickCreateType }>(EVENTS.quickCreate, (d) => {
      if (d?.type === type) fn();
    });
    return () => {
      if (handlers.get(type) === fn) handlers.delete(type);
      off();
    };
  }, [type, enabled]);

  useEffect(() => {
    const state = location.state as { quickCreate?: string } | null;
    if (!enabled || state?.quickCreate !== type) return;
    ref.current?.();
    // clear the state so a refresh / back does not reopen the dialog
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [enabled, type, location.state, location.pathname, location.search, navigate]);
}
